import { Grid, Switch, FormControlLabel } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import SettingsCard from "./SettingsCard";

const DEFAULTS = {
  cookie_consent_message:
    "We use cookies to improve your experience and analyze traffic. By accepting, you agree to our use of cookies.",
  cookie_consent_privacy_url: "",
  cookie_consent_button_label: "Accept Cookies",
  cookie_consent_bg: "#1f2937",
  cookie_consent_text_color: "#f9fafb",
  cookie_consent_button_bg: "#2563eb",
  cookie_consent_button_text_color: "#ffffff",
};

const AUTO_SAVE_TOAST_ID = "cookie-consent-autosave";
const TEXT_DEBOUNCE_MS = 650;
const COLOR_DEBOUNCE_MS = 320;

function cookieSliceFromCustomization(cust) {
  return {
    enabled: !!cust?.cookie_consent_enabled,
    trackingEnabled: cust?.visitor_tracking_enabled !== false,
    requiresConsent: !!cust?.visitor_tracking_requires_consent,
    message: cust?.cookie_consent_message || DEFAULTS.cookie_consent_message,
    privacyUrl: cust?.cookie_consent_privacy_url || "",
    buttonLabel: cust?.cookie_consent_button_label || DEFAULTS.cookie_consent_button_label,
    bg: cust?.cookie_consent_bg || DEFAULTS.cookie_consent_bg,
    textColor: cust?.cookie_consent_text_color || DEFAULTS.cookie_consent_text_color,
    buttonBg: cust?.cookie_consent_button_bg || DEFAULTS.cookie_consent_button_bg,
    buttonTextColor:
      cust?.cookie_consent_button_text_color || DEFAULTS.cookie_consent_button_text_color,
  };
}

function fingerprintFromValues(v) {
  return JSON.stringify({
    enabled: !!v.enabled,
    trackingEnabled: v.trackingEnabled !== false,
    requiresConsent: !!v.requiresConsent,
    message: (v.message || "").trim() || DEFAULTS.cookie_consent_message,
    privacyUrl: (v.privacyUrl || "").trim(),
    buttonLabel: (v.buttonLabel || "").trim() || DEFAULTS.cookie_consent_button_label,
    bg: (v.bg || "").trim() || DEFAULTS.cookie_consent_bg,
    textColor: (v.textColor || "").trim() || DEFAULTS.cookie_consent_text_color,
    buttonBg: (v.buttonBg || "").trim() || DEFAULTS.cookie_consent_button_bg,
    buttonTextColor: (v.buttonTextColor || "").trim() || DEFAULTS.cookie_consent_button_text_color,
  });
}

function CookieConsentBrandingSection() {
  const [agency, Update] = useAgencyInfo();
  const Service = useAppServices();
  const cust = agency?.Customization || {};

  const [enabled, setEnabled] = useState(!!cust.cookie_consent_enabled);
  const [trackingEnabled, setTrackingEnabled] = useState(
    cust.visitor_tracking_enabled !== false
  );
  const [requiresConsent, setRequiresConsent] = useState(
    !!cust.visitor_tracking_requires_consent
  );
  const [message, setMessage] = useState(
    cust.cookie_consent_message || DEFAULTS.cookie_consent_message
  );
  const [privacyUrl, setPrivacyUrl] = useState(cust.cookie_consent_privacy_url || "");
  const [buttonLabel, setButtonLabel] = useState(
    cust.cookie_consent_button_label || DEFAULTS.cookie_consent_button_label
  );
  const [bg, setBg] = useState(cust.cookie_consent_bg || DEFAULTS.cookie_consent_bg);
  const [textColor, setTextColor] = useState(
    cust.cookie_consent_text_color || DEFAULTS.cookie_consent_text_color
  );
  const [buttonBg, setButtonBg] = useState(
    cust.cookie_consent_button_bg || DEFAULTS.cookie_consent_button_bg
  );
  const [buttonTextColor, setButtonTextColor] = useState(
    cust.cookie_consent_button_text_color || DEFAULTS.cookie_consent_button_text_color
  );

  const valuesRef = useRef({});
  valuesRef.current = {
    enabled,
    trackingEnabled,
    requiresConsent,
    message,
    privacyUrl,
    buttonLabel,
    bg,
    textColor,
    buttonBg,
    buttonTextColor,
  };

  const lastSavedFingerprintRef = useRef(
    fingerprintFromValues(cookieSliceFromCustomization(cust))
  );
  const textDebounceRef = useRef(null);
  const colorDebounceRef = useRef(null);
  const agencyRef = useRef(agency);
  agencyRef.current = agency;

  useEffect(() => {
    const c = agency?.Customization || {};
    setEnabled(!!c.cookie_consent_enabled);
    setTrackingEnabled(c.visitor_tracking_enabled !== false);
    setRequiresConsent(!!c.visitor_tracking_requires_consent);
    setMessage(c.cookie_consent_message || DEFAULTS.cookie_consent_message);
    setPrivacyUrl(c.cookie_consent_privacy_url || "");
    setButtonLabel(c.cookie_consent_button_label || DEFAULTS.cookie_consent_button_label);
    setBg(c.cookie_consent_bg || DEFAULTS.cookie_consent_bg);
    setTextColor(c.cookie_consent_text_color || DEFAULTS.cookie_consent_text_color);
    setButtonBg(c.cookie_consent_button_bg || DEFAULTS.cookie_consent_button_bg);
    setButtonTextColor(
      c.cookie_consent_button_text_color || DEFAULTS.cookie_consent_button_text_color
    );
    lastSavedFingerprintRef.current = fingerprintFromValues(cookieSliceFromCustomization(c));
  }, [agency?._id, agency?.Customization]);

  useEffect(
    () => () => {
      if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
      if (colorDebounceRef.current) clearTimeout(colorDebounceRef.current);
    },
    []
  );

  const persist = useCallback(async () => {
    const ag = agencyRef.current;
    if (!ag?._id) {
      toast.error("Agency not loaded.");
      return;
    }
    const v = valuesRef.current;
    const fp = fingerprintFromValues(v);
    if (fp === lastSavedFingerprintRef.current) return;

    const payload = {
      _id: ag._id,
      Customization: {
        ...(ag.Customization || {}),
        cookie_consent_enabled: v.enabled,
        cookie_consent_message: (v.message || "").trim() || DEFAULTS.cookie_consent_message,
        cookie_consent_privacy_url: (v.privacyUrl || "").trim(),
        cookie_consent_button_label:
          (v.buttonLabel || "").trim() || DEFAULTS.cookie_consent_button_label,
        cookie_consent_bg: (v.bg || "").trim() || DEFAULTS.cookie_consent_bg,
        cookie_consent_text_color: (v.textColor || "").trim() || DEFAULTS.cookie_consent_text_color,
        cookie_consent_button_bg: (v.buttonBg || "").trim() || DEFAULTS.cookie_consent_button_bg,
        cookie_consent_button_text_color:
          (v.buttonTextColor || "").trim() || DEFAULTS.cookie_consent_button_text_color,
        visitor_tracking_enabled: v.trackingEnabled !== false,
        visitor_tracking_requires_consent: !!v.requiresConsent,
      },
    };

    const promise = Service.agency
      .update({ payload, toaster: false })
      .then(({ response }) => {
        if (!response?.data) throw new Error("Save failed");
        Update(response.data);
        lastSavedFingerprintRef.current = fp;
        return response;
      });

    toast.promise(
      promise,
      {
        loading: "Saving cookie banner…",
        success: "Cookie banner updated",
        error: (err) => err?.message || "Failed to save",
      },
      { id: AUTO_SAVE_TOAST_ID }
    );

    await promise.catch(() => {});
  }, [Service.agency, Update]);

  const scheduleTextPersist = useCallback(() => {
    if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
    textDebounceRef.current = setTimeout(() => {
      textDebounceRef.current = null;
      persist();
    }, TEXT_DEBOUNCE_MS);
  }, [persist]);

  const flushTextPersist = useCallback(() => {
    if (textDebounceRef.current) {
      clearTimeout(textDebounceRef.current);
      textDebounceRef.current = null;
      persist();
    }
  }, [persist]);

  const scheduleColorPersist = useCallback(() => {
    if (colorDebounceRef.current) clearTimeout(colorDebounceRef.current);
    colorDebounceRef.current = setTimeout(() => {
      colorDebounceRef.current = null;
      persist();
    }, COLOR_DEBOUNCE_MS);
  }, [persist]);

  return (
    <SettingsCard
      title="Cookie consent banner"
      subtitle="When the banner is enabled, Google Analytics and directory visitor analytics load only after acceptance. Use the visitor-tracking switches if you rely on an external CMP such as Consently. Changes save automatically."
      gridProps={{ xs: 12 }}
    >
      <SoftBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => {
                const next = e.target.checked;
                setEnabled(next);
                valuesRef.current = { ...valuesRef.current, enabled: next };
                persist();
              }}
              color="primary"
            />
          }
          label={enabled ? "Enabled" : "Disabled"}
          labelPlacement="start"
          sx={{ marginRight: 0, justifyContent: "space-between", width: "100%", ml: 0 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={trackingEnabled}
              onChange={(e) => {
                const next = e.target.checked;
                setTrackingEnabled(next);
                valuesRef.current = { ...valuesRef.current, trackingEnabled: next };
                persist();
              }}
              color="primary"
            />
          }
          label={
            trackingEnabled
              ? "Directory visitor analytics on"
              : "Directory visitor analytics off"
          }
          labelPlacement="start"
          sx={{ marginRight: 0, justifyContent: "space-between", width: "100%", ml: 0 }}
        />
        <SoftTypography variant="caption" sx={{ opacity: 0.75, mt: -1 }}>
          visitorId (localStorage) and sessionId (sessionStorage) are used only for directory
          traffic stats (unique visitors, sessions, time on page). They are not required for
          the site to work. Turn this off to never set those identifiers.
        </SoftTypography>

        <FormControlLabel
          control={
            <Switch
              checked={requiresConsent}
              onChange={(e) => {
                const next = e.target.checked;
                setRequiresConsent(next);
                valuesRef.current = { ...valuesRef.current, requiresConsent: next };
                persist();
              }}
              color="primary"
            />
          }
          label={
            requiresConsent
              ? "Wait for consent before tracking"
              : "Track until a CMP rejects analytics"
          }
          labelPlacement="start"
          sx={{ marginRight: 0, justifyContent: "space-between", width: "100%", ml: 0 }}
        />
        <SoftTypography variant="caption" sx={{ opacity: 0.75, mt: -1 }}>
          Enable this with an external CMP (Consently, Cookiebot, etc.). Identifiers are not
          created until consent is given. You can also call{" "}
          <code>window.sdaiSetAnalyticsConsent(true)</code> from the CMP analytics category.
        </SoftTypography>

        <SoftTypography variant="caption" sx={{ opacity: 0.75 }}>
          Banner text
        </SoftTypography>
        <SoftInput
          multiline
          rows={3}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            scheduleTextPersist();
          }}
          onBlur={flushTextPersist}
          placeholder={DEFAULTS.cookie_consent_message}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SoftTypography variant="caption" sx={{ opacity: 0.75, display: "block", mb: 0.5 }}>
              Privacy policy URL
            </SoftTypography>
            <SoftInput
              value={privacyUrl}
              onChange={(e) => {
                setPrivacyUrl(e.target.value);
                scheduleTextPersist();
              }}
              onBlur={flushTextPersist}
              placeholder="https://yoursite.com/privacy or /privacy"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SoftTypography variant="caption" sx={{ opacity: 0.75, display: "block", mb: 0.5 }}>
              Accept button label
            </SoftTypography>
            <SoftInput
              value={buttonLabel}
              onChange={(e) => {
                setButtonLabel(e.target.value);
                scheduleTextPersist();
              }}
              onBlur={flushTextPersist}
              placeholder={DEFAULTS.cookie_consent_button_label}
            />
          </Grid>
        </Grid>

        <SoftTypography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
          Colors
        </SoftTypography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <SoftTypography variant="caption" sx={{ opacity: 0.75, display: "block", mb: 0.5 }}>
              Banner background
            </SoftTypography>
            <SoftInput
              type="color"
              value={bg}
              onChange={(e) => {
                setBg(e.target.value);
                scheduleColorPersist();
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SoftTypography variant="caption" sx={{ opacity: 0.75, display: "block", mb: 0.5 }}>
              Text
            </SoftTypography>
            <SoftInput
              type="color"
              value={textColor}
              onChange={(e) => {
                setTextColor(e.target.value);
                scheduleColorPersist();
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SoftTypography variant="caption" sx={{ opacity: 0.75, display: "block", mb: 0.5 }}>
              Button background
            </SoftTypography>
            <SoftInput
              type="color"
              value={buttonBg}
              onChange={(e) => {
                setButtonBg(e.target.value);
                scheduleColorPersist();
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SoftTypography variant="caption" sx={{ opacity: 0.75, display: "block", mb: 0.5 }}>
              Button text
            </SoftTypography>
            <SoftInput
              type="color"
              value={buttonTextColor}
              onChange={(e) => {
                setButtonTextColor(e.target.value);
                scheduleColorPersist();
              }}
            />
          </Grid>
        </Grid>
      </SoftBox>
    </SettingsCard>
  );
}

export default CookieConsentBrandingSection;
